import React from "react";
import { Lead, Section, P, CTACard } from "../../../../blog/components";
import ctaBg from "../../../../assets/demo/screen-01.png";

export const title = "Trusting AI blindly";
export const excerpt =
  "We got comfortable merging fully AI-written work fast. But the moment an agent stops is not the moment the work is safe to ship. Once it merges, you still own it.";
export const heroAlt = "Moebius-style landscape: a lone tree on a green hillside above a valley, with pink clouds and a crescent moon at dusk";
export const readingTime = "4 min read";

const BLUE = "#5ba8f5";

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/* ── References ───────────────────────────────────────────────────────────── */

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "Anthropic, “Common workflows,” Claude Code Documentation. Used for Claude Code workflows around codebase exploration, testing, PR creation and review, scheduled tasks, worktrees, plan mode, and subagents.",
    href: "https://code.claude.com/docs/en/common-workflows",
    label: "code.claude.com/docs/en/common-workflows",
  },
  {
    text: "N. Perry, M. Srivastava, D. Kumar, D. Boneh, “Do Users Write More Insecure Code with AI Assistants?” (Stanford). Used for the finding that participants with an AI assistant wrote significantly less secure code, yet were more likely to believe their code was secure.",
    href: "https://arxiv.org/abs/2211.03622",
    label: "arxiv.org/abs/2211.03622",
  },
  {
    text: "Sonar, “State of Code Developer Survey report” (2026). Used for 61% of developers saying AI often produces code that looks correct but is not reliable, 96% not fully trusting AI output, and only 48% always verifying it: the verification bottleneck.",
    href: "https://www.sonarsource.com/blog/state-of-code-developer-survey-report-the-current-reality-of-ai-coding",
    label: "sonarsource.com/…/state-of-code-developer-survey-report",
  },
  {
    text: "Anthropic, “Code Review,” Claude Code Documentation. Used for multiple agents reviewing a pull request in parallel, a verification step that filters false positives, and findings deduplicated and ranked by severity.",
    href: "https://code.claude.com/docs/en/code-review",
    label: "code.claude.com/docs/en/code-review",
  },
  {
    text: "OpenSSF, “Security-Focused Guide for AI Code Assistant Instructions.” Used for the principle that the developer stays in control and responsible for AI-generated code, and should critically evaluate and edit it rather than blindly accept it.",
    href: "https://best.openssf.org/Security-Focused-Guide-for-AI-Code-Assistant-Instructions.html",
    label: "best.openssf.org/…/AI-Code-Assistant-Instructions",
  },
];

export default function Post() {
  return (
    <>
      <Lead>
        We adapted quite fast to writing code with AI. But to me, it&rsquo;s surprising how fast we got
        comfortable with just accepting and merging fully AI-written features and reworks. (That&rsquo;s how I
        feel.)
      </Lead>

      <P>
        Claude Workflow, is not really about generating a function anymore, its about using Claude to explore
        an unfamiliar codebase, trace an auth flow, add tests, open a pull request, resume old sessions, run
        parallel work in worktrees, plan before it edits, hand research to subagents, even run scheduled jobs
        like reviewing open PRs.<Fn n={1} /> That is closer to a junior developer who moves through the repo and
        leaves behind a trail that looks like work.
      </P>
      <P>
        And that is where it gets uncomfortable. When an agent finishes, the output usually has the shape of
        finished work: a plan, changed files, commands that ran, a summary, sometimes a PR description, sometimes
        test output, sometimes a note about what risks to watch. Giving the work the look and feel of finished
        work. I often catch myself trusting this output blindly. Just of pure idleness. Even tough I know that
        LLM output is still just mathematical probability not the truth.<Fn n={1} /> The output can be useful and
        still not be final.
      </P>
      <P>
        The work is not fake. It is not nonsense. Most of the time it is plausible and coherent, it compiles, it
        usually passes tests. The agent did something, maybe even the right thing. But still could cause bugs, errors
        and worse.<Fn n={2} />
      </P>

      <Section title="What about reviews?">
        <P>
          With autocomplete, the early generation of tools like GitHub Copilot, Cursor and Windsurf, reviewing
          was still part of the process and small by default. One suggestion, a few lines, accept or reject. Even
          when it was wrong, you were standing right next to the code, so the mistake stayed local. Today those
          same tools ship whole features and big chunks at once, often multiple thousand lines of code in a single
          run, and the review never shrank to match.
        </P>
        <P>
          Working in Agentic Workflows or multi Agent Workflows like Claude Code Dynamic Workflows isn&rsquo;t the
          same thing. The agent searches, edits, runs a test, reads the error, edits again, maybe installs a
          package, maybe opens a branch, then summarizes the whole work in a tone that sounds final (getting
          worse with agent swarms). So the question changes. You are no longer only asking whether the code is
          correct. You are asking whether this was the right path through the problem. Did it read the right
          files? Did it misread the architecture? Did it take a shortcut because the tests were weak? Did it
          change public behavior while only checking the happy path?
        </P>
        <P>
          That is way harder to review because half of what you need to review is invisible. The diff is there;
          the reasoning behind it mostly is not. Plan mode and subagents help: the agent proposes a plan before
          touching files, and can investigate in a seperate context and report back.<Fn n={1} /> The more capable
          the agent, the more the human needs a record of the path, not only the patch.
        </P>
      </Section>

      <Section title="Done doesn&rsquo;t mean done anymore">
        <P>
          We still say &ldquo;done&rdquo; like it means one thing. But in agentic coding &ldquo;done&rdquo; is not
          one state, it is a whole stack of separate claims. The agent stopped. The code compiles. The tests
          pass. The risk is acceptable. The developer actually understands the change. An agent can hit a few of
          those and quietly miss the rest. It can solve the task and still leave a hole. It can run tests and
          still skip the exact behavior that mattered. To me that is the part that gets blurred together:
          &ldquo;the agent stopped&rdquo; and &ldquo;this is safe to ship&rdquo; feel like the same moment, but
          they are not and draws the line between vibe coding and coding.
        </P>
        <P>
          So &ldquo;agents will replace developers&rdquo; is not really the wrong fear to me. It will cost dev
          jobs, it already does, especially for junior devs. But the framing is still off. The real problem is
          duller and runs underneath all of it: agents raise how much code we produce faster than they raise how
          much code we can actually verify.<Fn n={3} /> That gap has a name. It is review debt, and it is growing.
        </P>
      </Section>

      <Section title="You still own it">
        <P>
          The agent can write the patch, explain it, run the test, even review it. You still own the result the
          moment it merges.<Fn n={5} /> You cannot tell a customer the bug is less real because an agent wrote it. You cannot
          tell the next engineer that nobody understands the code because Claude wrote it at 1:37 AM and the PR
          looked fine.
        </P>
        <P>
          Ownership was never about typing every character anyway. We already lean on libraries, frameworks,
          generated clients, scaffolds, CI. The job was always judgment. Agents just make that harder to ignore.
        </P>
        <P>
          More and more the reviewer is another agent. Agent writes the code, agent reviews the code.<Fn n={4} /> That can
          work, but it can also just stack two confident summaries on top of each other and call it consensus.
          When my co-founder (brother) and I started working on Cate, and on other projects before it, we ran
          into all of this ourselves. We merged things too fast because the diff looked fine and we wanted to
          make progress fast. Especially with the last couple of releases of Cate, where the app got more filled
          with features and more polished, a lot of bugs and duplicated architecture systems and boilerplate code
          surfaced. We cleaned it up in long, hard and still ongoing work. But what we learned and took from it is
          that we want to make code reviewing inside Cate one of the primary focus points in the near future. And
          we think that with the open canvas concept we have the best foundation, and enough &bdquo;space&rdquo;,
          to make this as visual and efficient as possible.
        </P>
        <P>
          Make agent work inspectable: show the task, show the plan, show the files it read and changed, show the
          commands, show the test output, show the approvals, and show the gaps. Give it as a clear visual
          overview on the canvas, so the chain of work stays visible while you review it, instead of being buried
          in a chat log or a confident paragraph.
        </P>
        <P>Then let the developer review it like they own it.</P>
        <p style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          Because they do.
        </p>
      </Section>

      <CTACard download github bgImage={ctaBg} />

      <Section title="References">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
